import config from "@Config/index";
import { logAppEvent } from "@Utils/appAnalytics";
import { posthogReset } from "@Utils/posthog";
import { APP_EVENTS, APP_PAGE, WEB_VIEW_APP_EVENTS } from "@Utils/webApputils/analyticEvents";
import { sendWebViewEvent } from "@Utils/webviewEvents";
import axios from "axios";
import { isNewNativeAppShell, parseAppShell } from "constants/appShell";
import { isApp } from "hooks/useIsApp";

const generateOtpRequest = async (
  email: string,
  captchaToken: any = "",
  with_visual_challenge = false,
  isPartnerLogin = false,
) => {
  try {
    const { data } = await axios.post(
      isPartnerLogin
        ? `${config.API_URL}/api/v0/external_service/generate_email_otp`
        : `${config.API_URL}/api/v0/users/generate_email_otp`,
      {
        user: { email },
      },
      {
        params: {
          recaptcha_token: captchaToken,
          recaptcha_action: "email_login",
          with_visual_challenge,
        },
        withCredentials: true,
      },
    );
    return { data };
  } catch (error) {
    console.error(error);
    return { error };
  }
};

export const generateMobileOtpRequest = async (
  number: string,
  token: string | unknown = "",
  with_visual_challenge = false,
) => {
  try {
    const { data } = await axios.post(
      `${config.API_URL}/api/v0/users/generate_phone_otp`,
      {
        user: { phone: number },
      },
      {
        params: {
          recaptcha_token: token,
          recaptcha_action: "phone_login",
          with_visual_challenge,
        },
      },
    );
    return { data };
  } catch (error: any) {
    console.error(error);
    return {
      error: error?.response?.data?.message?.user_friendly_message || "Something went wrong",
    };
  }
};

export const verifyOtpRequest = async (email: string, otp: string) => {
  try {
    const response = await axios.post(
      `${config.API_URL}/api/v0/users/login_email_otp`,
      {
        user: { email, otp },
      },
      {
        withCredentials: true,
      },
    );

    return { data: response.data };
  } catch ({ response }: any) {
    const res: any = response;
    return { error: res?.data?.message?.user_friendly_message };
  }
};

export const verifyMobileOtpRequest = async (phone: string, otp: string, sessionId: string) => {
  try {
    const response = await axios.post(
      `${config.API_URL}/api/v0/users/login_phone_otp`,
      {
        user: { phone, otp, session_uuid: sessionId },
      },
      {
        withCredentials: true,
      },
    );
    return { data: response.data };
  } catch (error: any) {
    return { error: error?.response?.data?.message?.user_friendly_message };
  }
};

export const logoutRequest = async () => {
  try {
    const response = await axios.post(
      `${config.API_URL}/api/v0/users/logout`,
      {},
      {
        withCredentials: true,
      },
    );
    if (response.data?.message === "logged-out") {
      window.document.cookie = `user_auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; max-age=0;`;
      // send event to app to clear token state
      if (isApp()) {
        window.localStorage?.removeItem("user_auth_token");
        sendWebViewEvent(WEB_VIEW_APP_EVENTS.LOGOUT_SUCCESS);
        logAppEvent({
          eventName: APP_EVENTS.USER_LOGOUT,
          payload: {
            category: APP_PAGE.PROFILE,
          },
        });
      }
      // Reset PostHog identity only for new native app (has native PostHog SDK for profile continuity)
      if (
        typeof window !== "undefined" &&
        isNewNativeAppShell(parseAppShell((window as any).appShell))
      ) {
        posthogReset();
      }
    }
    return { data: response.data };
  } catch ({ response }: any) {
    const res: any = response;
    return { error: res?.data?.message?.user_friendly_message };
  }
};

export default generateOtpRequest;
